// @flow

import { computed, observable } from "mobx";

import Model from "app/models/Model";
import TimelineItemComment from "app/models/TimelineItemComment";
import VideoSources from "timelinely-player/TimelinelyPlayer";

export const TYPES = {
    AUDIO: "audio",
    IMAGE: "image",
    MAP: "google_maps",
    TEXT: "text",
    URL: "url",
    VIDEO: "video",
    YOUTUBE: "youtube",
    WIZARD: "wizard"
};

export const REACTIONS = {
    LIKE: 1,
    DISLIKE: 2
};

export const STATS = {
    HOVER: 1,
    CLICK: 2
};

export const INTERACTIVE_TYPES = [
    TYPES.AUDIO,
    TYPES.VIDEO,
    TYPES.YOUTUBE
];

export const MINIMUM_DURATION_SECONDS = 5;

export type TimelineItemMetadata = {
    title: ?string,
    image: ?string,
    description: ?string,
    video: ?VideoSources
};

export type TimelineItemMetaImage = {
    type: "image" | "favicon",
    src: string
};

export default class TimelineItem extends Model {
    static key = 1;
    key: number;

    rowid: number;
    video_id: number;

    @observable type: string;
    @observable type_id: string;

    @observable url: string;
    @observable image: string;
    @observable metadata: ?TimelineItemMetadata;
    @observable comment: string;

    @observable start_time: number;
    @observable stop_time: number;

    @observable clickthroughs: number;
    @observable total_likes: ?number;
    @observable total_dislikes: ?number;
    @observable user_reaction: ?number;

    @observable comments: TimelineItemComment[] = [];

    time: string;
    created_at: ?Date;

    constructor() {
        super();
        this.key = this.constructor.key++;
    }

    normalize() {
        this.normalizeInt(
            "start_time",
            "stop_time",
            "clickthroughs",
            "total_likes",
            "total_dislikes",
            "user_reaction"
        );
        this.normalizeDate("created_at");
        this.comments = TimelineItemComment.makeArray(this.comments);
    }

    getMeta(property: string): string {
        return (this.metadata ? this.metadata : {})[property];
    }

    inRange(time: number) {
        return this.start_time <= time && this.stop_time >= time;
    }

    /*
     * Computed
     */

    @computed
    get isSaved(): boolean {
        return !!this.rowid;
    }

    @computed
    get metaTitle(): string | null {
        return this.getMeta("title");
    }

    @computed
    get metaDescription(): string | null {
        return this.getMeta("description");
    }

    @computed
    get metaCoords(): string | null {
        return this.getMeta("coords");
    }

    @computed
    get metaUrl(): string | null {
        return this.getMeta("url");
    }

    @computed
    get metaImage(): ?TimelineItemMetaImage {
        return this.constructor.metaImage(this.getMeta("image"), this.url);
    }

    static metaImage(
        metaImage: ?string,
        domain: ?string
    ): ?TimelineItemMetaImage {
        if (metaImage && metaImage.length) {
            return {
                type: "image",
                src: metaImage
            };
        }
        
        if (domain) {
            return {
                type: "favicon",
                src: "https://www.google.com/s2/favicons?sz=64&domain=" + domain
            };
        }

        return undefined;
    }

    @computed
    get centralTime(): number {
        return (this.stop_time + this.start_time) / 2;
    }

    @computed
    get duration(): number {
        return this.stop_time - this.start_time;
    }

    @computed
    get isWebcamRecording(): boolean {
        return this.type === 'video' && !!this.image && !!this.image.match(/\.(mp4|webm|mkv)$/);
    }
}



// WEBPACK FOOTER //
// ./src/app/models/TimelineItem.js